import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  SortModelItem
} from 'ag-grid-community';
import { isEmpty } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { HeaderData } from 'adapters/types';
import { DEFAULT_PAGE_NO } from 'common/constants';
import { useClient } from 'components/ClientProvider';
import { getStoreId } from 'components/StoreSelect/slice';
import { onErrorHandler } from 'hooks/helper';
import { setPrintChecksResponse } from 'pages/VendorsTablePage/components/PrintChecks';
import { PrintChecksVendorData } from 'pages/VendorsTablePage/components/PrintChecks/types';
import { errors } from 'pages/VendorsTablePage/messages';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  HIGH_VOLUME_PRINT_CHECKS_PAGE_SIZE,
  SORT_PRINT_CHECKS_COLUMN,
  SORT_ORDER_PARAMS
} from '../constants';
import { PrintChecksServerSideDatasourceType } from '../types';

function getPage(request: IServerSideGetRowsRequest) {
  return request.startRow
    ? Math.round(request.startRow / HIGH_VOLUME_PRINT_CHECKS_PAGE_SIZE) + 1
    : DEFAULT_PAGE_NO;
}

function handleSortChange(
  newColumnState: SortModelItem,
  prevSortColumn: React.MutableRefObject<string | undefined>,
  prevSortOrder: React.MutableRefObject<string | undefined>
) {
  const sortColumn = newColumnState
    ? SORT_PRINT_CHECKS_COLUMN[newColumnState.colId]
    : undefined;
  const sortOrder = newColumnState
    ? SORT_ORDER_PARAMS[newColumnState.sort]
    : undefined;
  const sortChanged =
    sortColumn !== prevSortColumn.current ||
    sortOrder !== prevSortOrder.current;
  return { sortChanged, sortColumn, sortOrder };
}

export const usePrintChecksServerSideDatasource = ({
  searchValue
}: PrintChecksServerSideDatasourceType): {
  isFilterChanged: boolean;
  isLoading: boolean;
  serverSideDatasource: IServerSideDatasource;
  gridRowData: PrintChecksVendorData[] | undefined;
  columnState: SortModelItem | undefined;
} => {
  const intl = useIntl();
  const client = useClient();
  const dispatch = useAppDispatch();
  const storeId = useAppSelector(getStoreId);
  const [isLoading, setIsLoading] = useState(false);
  const [columnState, setColumnState] = useState<SortModelItem>();
  const [gridRowData, setGridRowData] = useState<PrintChecksVendorData[]>([]);
  const prevSortColumn = useRef<string | undefined>(undefined);
  const prevSortOrder = useRef<string | undefined>(undefined);
  const isFilterChangedRef = useRef(true);
  const filterData = useMemo(() => {
    isFilterChangedRef.current = true;
    return {
      storeId,
      searchValue
    };
  }, [searchValue, storeId]);
  const serverSideDatasource = useMemo(
    () => ({
      getRows: async ({
        api,
        request,
        success,
        fail
      }: IServerSideGetRowsParams<PrintChecksVendorData>) => {
        if (isEmpty(client?.headers['x-department-name'])) {
          return success({ rowData: [] });
        }
        const newColumnState = request.sortModel[0];
        setColumnState(newColumnState);
        const headers: HeaderData = {
          ...client?.headers
        };
        const page = getPage(request);

        const { sortChanged, sortColumn, sortOrder } = handleSortChange(
          newColumnState,
          prevSortColumn,
          prevSortOrder
        );
        if (isFilterChangedRef.current || sortChanged) {
          api.showLoadingOverlay();
        }
        try {
          setIsLoading(true);
          const result =
            await client?.data.fetchHighVolumePrintAuthorizedVendors(
              headers,
              filterData.storeId,
              page,
              HIGH_VOLUME_PRINT_CHECKS_PAGE_SIZE,
              filterData.searchValue,
              sortColumn,
              sortOrder
            );
          const rowData: PrintChecksVendorData[] =
            result?.vendors.map(vendorData => ({
              ...vendorData,
              vendorNo: vendorData.vendorNo,
              vendorName: vendorData.vendorName,
              invoices: vendorData.invoices,
              netAmount: vendorData.totalNetAmount
            })) ?? [];
          if (result) dispatch(setPrintChecksResponse(result));
          setGridRowData(prevStateArray => [...prevStateArray, ...rowData]);
          setIsLoading(false);
          success({ rowData });
          api.hideOverlay();
          isFilterChangedRef.current = false;
          prevSortColumn.current = sortColumn;
          prevSortOrder.current = sortOrder;
          return undefined;
        } catch (err: any) {
          onErrorHandler(
            err,
            dispatch,
            intl,
            intl.formatMessage(errors.vendorApiFailureMsg)
          );
          return fail();
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client?.headers, client?.data, storeId, searchValue, dispatch, intl]
  );
  return useMemo(
    () => ({
      isFilterChanged: isFilterChangedRef.current,
      isLoading,
      serverSideDatasource,
      gridRowData,
      columnState
    }),
    [columnState, gridRowData, isLoading, serverSideDatasource]
  );
};
